import { useEffect, useState, useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { omit } from 'ramda';

import { render } from 'src/tools/render.tools';
import { t } from 'src/tools/text.tools';
import { ifTrue } from 'src/tools/logic.tools';
import { logUnableToProceed } from 'src/tools/log.tools';

import { clearInputUsers, setInputAddedUsers, setInputInvitedUsers } from 'src/store/team/team.reducer';
import { FormParserContext } from 'src/contexts/form.context';
import { ResizableWindowContext } from 'src/contexts/resizableWindow.context';
import { getCustomIsLoading, getCustomIsUpdating } from 'src/store/loading/loading.reducer';
import { getInputAddedUsers, getInputInvitedUsers, getShowInputUsers } from 'src/store/team/team.getters';
import { requestUserRoles, createTeamMembers } from 'src/store/team/team.actions';
import { InputAddUser, InputInviteUser, Result } from 'src/models';
import { ResourceData } from 'src/models/modal.model';

import { Accordion } from 'src/components/Accordion';
import { AssignMember } from '../AssignMember';
import { InviteUser } from '../InviteUser/InviteUser';
import { Label, EmailInput } from 'src/components/FormRenderers';
import { Loading } from 'src/components/Loading';
import { ModalDialog } from 'src/components/ModalDialog';

import { CONFIG } from 'src/config';

import classes from './BuildTeamDialog.module.scss';

const { addMembersMax } = CONFIG.team;

const TXT = t(['members', 'modal']);

interface Props {
  resource: ResourceData;
}

export function BuildTeamDialog({ resource }: Props) {
  const dispatch = useDispatch();

  const { closeWindow } = useContext(ResizableWindowContext);

  const isLoadingRoles = useSelector(getCustomIsLoading)('roles');

  const isSubmitting = useSelector(getCustomIsUpdating)('team');

  const addedUsers = useSelector(getInputAddedUsers);

  const invitedUsers = useSelector(getInputInvitedUsers);

  const showInputUsers = useSelector(getShowInputUsers);

  const [searchString, setSearchString] = useState('');

  useEffect(() => {
    if (resource) dispatch(requestUserRoles(resource?.resource_id, resource?.resource_type));

    return () => {
      dispatch(clearInputUsers());
    };
  }, [dispatch, resource]);

  function handleOnChange(s: string) {
    setSearchString(s);
  }

  function handleProcessResult(result: Result) {
    if (result.isOK) {
      closeWindow();
    } else {
      logUnableToProceed('handleProcessResult', { result });
    }
  }

  function handleSubmit() {
    if (resource)
      dispatch(
        createTeamMembers(
          resource?.resource_id,
          resource?.resource_type,
          {
            team_members: addedUsers?.map(u => omit(['details'], u)) ?? [],
            invites: invitedUsers ?? [],
          },
          { callback: handleProcessResult },
        ),
      );
  }

  function renderInput() {
    if (!resource) {
      logUnableToProceed('BuildTeamDialog::renderInput', { resource });
      return;
    }

    return (
      <div className={classes.input}>
        <FormParserContext.Provider
          value={{
            value: searchString,
            fieldName: 'email',
            script: { label: 'Email', isRequired: true, autofocus: true },
            onChange: handleOnChange,
            resource_id: resource.resource_id,
            resource_type: resource.resource_type,
            isDisabled: showInputUsers === addMembersMax,
          }}
        >
          <Label />
          <EmailInput />
        </FormParserContext.Provider>
      </div>
    );
  }

  function getHandleAddedUserOnChange(user: InputAddUser) {
    return function call(updatedUser: InputAddUser) {
      dispatch(setInputAddedUsers(addedUsers?.map(u => (u === user ? updatedUser : u))));
    };
  }

  function getHandleAddedUserOnDelete(user: InputAddUser) {
    return function call() {
      dispatch(setInputAddedUsers(addedUsers?.filter(u => u !== user)));
    };
  }

  function renderAddedUser(user: InputAddUser) {
    return (
      <AssignMember
        key={user.id}
        user={user}
        onChange={getHandleAddedUserOnChange(user)}
        onDelete={getHandleAddedUserOnDelete(user)}
        resource={resource}
      />
    );
  }

  function getHandleInvitedUserOnChange(user: InputInviteUser) {
    return function call(updatedUser: InputInviteUser) {
      dispatch(setInputInvitedUsers(invitedUsers?.map(u => (u === user ? updatedUser : u))));
    };
  }

  function getHandleInvitedUserOnDelete(user: InputInviteUser) {
    return function call() {
      dispatch(setInputInvitedUsers(invitedUsers?.filter(u => u !== user)));
    };
  }

  function renderInvitedUser(user: InputInviteUser) {
    return (
      <InviteUser
        key={user.email}
        user={user}
        onChange={getHandleInvitedUserOnChange(user)}
        onDelete={getHandleInvitedUserOnDelete(user)}
        resource={resource}
      />
    );
  }

  function renderAddedUserSection() {
    return (
      <Accordion classNames={{ container: classes.accordion }} label={TXT('assignTeamMembers')} isOpenDefault>
        {render(addedUsers, renderAddedUser)}
      </Accordion>
    );
  }

  function renderInvitedUserSection() {
    return (
      <Accordion classNames={{ container: classes.accordion }} label={TXT('inviteNewUser')} isOpenDefault>
        {render(invitedUsers, renderInvitedUser)}
      </Accordion>
    );
  }

  function renderLoading() {
    return <Loading isFull />;
  }

  function renderContent() {
    return (
      <>
        {renderInput()}
        {ifTrue(addedUsers?.length, renderAddedUserSection)}
        {ifTrue(invitedUsers?.length, renderInvitedUserSection)}
      </>
    );
  }

  return (
    <ModalDialog
      title={`${TXT('title')} (${showInputUsers} of ${addMembersMax})`}
      primaryButton={[TXT(['buttons', 'save']), handleSubmit, !showInputUsers || isSubmitting]}
      secondaryButton={[TXT(['buttons', 'cancel']), 'default']}
    >
      {ifTrue(!isLoadingRoles, renderContent)}
      {ifTrue(isLoadingRoles, renderLoading)}
    </ModalDialog>
  );
}
